import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as FAQIcon,
  Book as BookIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  Assignment as QueryIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../constants';

const Help = () => {
  const [expanded, setExpanded] = useState('panel1');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const quickLinks = [
    { title: 'Getting Started', icon: <DashboardIcon />, color: colors.primary },
    { title: 'Airport Authority of India', icon: <QueryIcon />, color: colors.info },
    { title: 'Notifications', icon: <NotificationsIcon />, color: colors.success },
    { title: 'Security & Access', icon: <SecurityIcon />, color: colors.warning },
  ];

  const faqSections = [
    {
      title: 'Getting Started',
      items: [
        {
          question: 'How do I create a new query?',
          answer: 'Navigate to "Raise Query" from the sidebar. Fill in the required details including query type, assignee, project, and description. Click "Submit" to create the query.',
        },
        {
          question: 'How do I view my assigned queries?',
          answer: 'Go to "Assigned Queries" from the sidebar to see all queries assigned to you. You can filter and sort them by status, priority, or date.',
        },
        {
          question: 'What is a unique query ID?',
          answer: 'Each query is automatically assigned a unique ID when created. This ID is used for tracking, audit logs, and SLA monitoring throughout the query lifecycle.',
        },
      ],
    },
    {
      title: 'Airport Authority of India',
      items: [
        {
          question: 'How do I respond to a query?',
          answer: 'Open the query from "Assigned Queries" and click "Respond". You can add text, attach documents, images, or other evidence. Click "Submit Response" to send your reply.',
        },
        {
          question: 'Can I delegate a query to someone else?',
          answer: 'Yes, you can delegate queries to other users. Click on the query and select "Delegate". Choose the user you want to delegate to and add a note if needed.',
        },
        {
          question: 'How do I escalate a query?',
          answer: 'Queries can be escalated manually or automatically based on SLA rules. To manually escalate, open the query and click "Escalate". The system will notify the escalation assignee.',
        },
        {
          question: 'Can I reopen a closed query?',
          answer: 'Yes, you can reopen a closed query if you need to add more information or if clarification is needed. Open the query and click "Reopen".',
        },
      ],
    },
    {
      title: 'Notifications',
      items: [
        {
          question: 'How do I receive notifications?',
          answer: 'You will receive notifications via Email, WhatsApp, and in-app notifications when queries are assigned to you, when someone responds, or when queries are escalated.',
        },
        {
          question: 'Can I customize notification preferences?',
          answer: 'Yes, go to Settings > Notifications to customize which notifications you receive and how you receive them (Email, WhatsApp, or in-app).',
        },
        {
          question: 'What triggers automatic reminders?',
          answer: 'Automatic reminders are sent based on SLA rules and query type. Reminders are sent to ensure queries are responded to within the defined SLA timeframe.',
        },
      ],
    },
    {
      title: 'Roles & Permissions',
      items: [
        {
          question: 'What are the different user roles?',
          answer: 'The system supports multiple roles including Admin, Chairperson, Responder, and others. Each role has different permissions and access levels.',
        },
        {
          question: 'How do I manage users?',
          answer: 'Admin users can create, edit, and manage users from the "Users" page in the Admin section. You can assign roles and manage permissions from there.',
        },
        {
          question: 'What can I see in my dashboard?',
          answer: 'Your dashboard shows queries relevant to your role. Admins see all queries, Chairpersons see queries they raised, and Responders see queries assigned to them.',
        },
      ],
    },
  ];

  const featureGuides = [
    {
      title: 'Query Types & Categorization',
      description: 'Queries can be categorized based on type, project, or other criteria. This helps in better organization and SLA tracking.',
    },
    {
      title: 'SLA Monitoring',
      description: 'The system tracks SLA compliance for each query. You can view SLA status and analytics in the SLA Dashboard.',
    },
    {
      title: 'Audit Trail',
      description: 'Every action on a query is logged, including status changes, responses, escalations, and delegations. View the complete thread in Query Details.',
    },
    {
      title: 'Project-Wise View',
      description: 'View queries organized by project to get a comprehensive view of all queries related to a specific project.',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.background,
        p: spacing.md,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        {/* Header */}
        <Box sx={{ mb: spacing.lg, mt: 0 }}>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: fonts.fontSizeMd,
              mb: spacing.sm,
            }}
          >
            Find answers and learn how to use the Airport Authority of India
          </Typography>
        </Box>

        {/* Quick Links */}
        <Box sx={{ mb: spacing.lg }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: fonts.weightSemiBold,
              fontSize: fonts.fontSizeLg,
              color: colors.textPrimaryDark,
              mb: spacing.sm,
            }}
          >
            Quick Links
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: spacing.sm,
              mb: spacing.md,
            }}
          >
            {quickLinks.map((link, index) => (
              <Card
                key={index}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: shadows.sm,
                  borderRadius: borderRadius.md,
                  '&:hover': {
                    boxShadow: shadows.cardHover,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: spacing.sm,
                    '&:last-child': { pb: spacing.sm },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.sm,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: borderRadius.md,
                        backgroundColor: `${link.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ color: link.color, fontSize: fonts.fontSizeXl }}>
                        {link.icon}
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: fonts.weightMedium,
                        fontSize: fonts.fontSizeMd,
                        color: colors.textPrimaryDark,
                      }}
                    >
                      {link.title}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Feature Guides */}
        <Box sx={{ mb: spacing.lg }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: fonts.weightSemiBold,
              fontSize: fonts.fontSizeLg,
              color: colors.textPrimaryDark,
              mb: spacing.sm,
            }}
          >
            Feature Guides
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: spacing.sm,
            }}
          >
            {featureGuides.map((guide, index) => (
              <Card
                key={index}
                sx={{
                  boxShadow: shadows.sm,
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.borderDefault}`,
                }}
              >
                <CardContent sx={{ p: spacing.sm }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.sm,
                      mb: spacing.xs,
                    }}
                  >
                    <BookIcon
                      sx={{
                        color: colors.primary,
                        fontSize: fonts.fontSizeLg,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: fonts.weightSemiBold,
                        fontSize: fonts.fontSizeMd,
                        color: colors.textPrimaryDark,
                      }}
                    >
                      {guide.title}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: colors.textSecondary,
                      fontSize: fonts.fontSizeMd,
                      lineHeight: fonts.lineHeightNormal,
                    }}
                  >
                    {guide.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              mb: spacing.sm,
            }}
          >
            <FAQIcon
              sx={{
                color: colors.primary,
                fontSize: fonts.fontSizeLg,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: fonts.weightSemiBold,
                fontSize: fonts.fontSizeLg,
                color: colors.textPrimaryDark,
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Box>

          {faqSections.map((section, sectionIndex) => (
            <Box key={sectionIndex} sx={{ mb: spacing.md }}>
              <Typography
                sx={{
                  fontWeight: fonts.weightSemiBold,
                  fontSize: fonts.fontSizeMd,
                  color: colors.textPrimaryDark,
                  mb: spacing.xs,
                  mt: sectionIndex > 0 ? spacing.md : 0,
                }}
              >
                {section.title}
              </Typography>
              <Divider sx={{ mb: spacing.sm }} />
              {section.items.map((item, itemIndex) => (
                <Accordion
                  key={itemIndex}
                  expanded={expanded === `panel${sectionIndex}-${itemIndex}`}
                  onChange={handleChange(`panel${sectionIndex}-${itemIndex}`)}
                  sx={{
                    mb: spacing.xs,
                    boxShadow: shadows.sm,
                    borderRadius: borderRadius.md,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: `${spacing.xs} 0`,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: colors.textSecondary }} />}
                    sx={{
                      px: spacing.sm,
                      py: spacing.xs,
                      minHeight: '48px',
                      '&:hover': {
                        backgroundColor: colors.backgroundSoft,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: fonts.weightMedium,
                        fontSize: fonts.fontSizeMd,
                        color: colors.textPrimaryDark,
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      px: spacing.sm,
                      pb: spacing.sm,
                      backgroundColor: colors.backgroundLight,
                    }}
                  >
                    <Typography
                      sx={{
                        color: colors.textSecondary,
                        fontSize: fonts.fontSizeMd,
                        lineHeight: fonts.lineHeightNormal,
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))}
        </Box>

        {/* Contact Support */}
        <Card
          sx={{
            mt: spacing.lg,
            boxShadow: shadows.md,
            borderRadius: borderRadius.card,
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
          }}
        >
          <CardContent sx={{ p: spacing.md }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: spacing.sm,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: fonts.weightBold,
                    fontSize: fonts.fontSizeLg,
                    color: colors.textWhite,
                    mb: spacing.xs,
                  }}
                >
                  Still need help?
                </Typography>
                <Typography
                  sx={{
                    color: colors.textWhite,
                    fontSize: fonts.fontSizeMd,
                    opacity: 0.9,
                  }}
                >
                  Contact your system administrator or IT support team for additional assistance.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Help;
